'use client'

import { createContext, useContext, type ReactNode } from 'react'

export interface BootstrapPlan {
  planName: string
  status: string
  baseOrderLimit: number
  currentPeriodEnd: string | null
  isCustom: boolean
}

export interface BootstrapUsage {
  allowed: number
  used: number
  remaining: number
  periodStart: string | null
  periodEnd: string | null
}

export interface BootstrapLimits {
  allowed: number
}

export interface BootstrapDashboard {
  current: unknown
  previous: unknown
}

/** Server-resolved default date range (UTC) so dashboard and bootstrap use the same key. */
export interface DashboardDefaultRange {
  from: string
  to: string
  timeZone?: string
}

export interface BootstrapData {
  sessionReady: boolean
  billingActive: boolean
  plan: BootstrapPlan
  /** Normalized plan key for gating: free | growth | pro | elite */
  planNormalized: string
  /**
   * `unknown` when plan/features are not authoritative (timeout/minimal fallback). UI must not treat as real FREE for upgrade gating.
   * Omitted or `known` = trust server plan/features for FeatureGate.
   */
  planResolution?: 'known' | 'unknown'
  /** Feature flags from DB (plan); UI shows upgrade instantly when false */
  features: Record<string, boolean>
  usage: BootstrapUsage | null
  limits: BootstrapLimits
  dashboard: BootstrapDashboard | null
  /** Server default range (YYYY-MM-DD) so page uses same key as bootstrap; avoids UTC-day mismatch */
  dashboardDefaultRange: DashboardDefaultRange | null
  /** Reserved for future admin nav gating; server currently returns true for all authenticated shops. */
  adminAllowed?: boolean
}

export interface BootstrapState {
  loading: boolean
  error: string | null
  reauthUrl: string | null
  data: BootstrapData | null
}

const BootstrapContext = createContext<BootstrapState | null>(null)

export function BootstrapProvider({
  children,
  value,
}: {
  children: ReactNode
  value: BootstrapState
}) {
  return (
    <BootstrapContext.Provider value={value}>
      {children}
    </BootstrapContext.Provider>
  )
}

export function useBootstrap(): BootstrapState {
  const ctx = useContext(BootstrapContext)
  if (!ctx) {
    return {
      loading: true,
      error: null,
      reauthUrl: null,
      data: null,
    }
  }
  return ctx
}
